
// --------New verison------------

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import CountUp from "react-countup";
import {
  Calendar,
  FileText,
  HeadphonesIcon,
  ChevronDown,
  Star,
  ArrowRight,
  Newspaper,
  MapPin,
  Building2,
  Sparkles,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import passport from "../assets/passport_service.png";
import oci from "@/assets/oci_service.png";
import consular from "@/assets/consular_services.png";
import visa from "@/assets/visa_service.png";

import CalendarView from "../components/booking/CalendarView";
import { centersAPI, newsAPI, testimonialsAPI } from "../utils/api";

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [inView, setInView] = useState(false);
  const [showCenterSelection, setShowCenterSelection] = useState(false);
  const [centers, setCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [news, setNews] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [menuData, setMenuData] = useState([]);

  const SERVICE_IMAGES = {
    "Visa": visa,
    "Passport": passport,
    "Consular": consular,
    "OCI": oci,
  };

  useEffect(() => {
    const loadPublicData = async () => {
      try {
        const [newsRes, testimonialsRes] = await Promise.all([
          newsAPI.getAll({ featured_only: false }).catch(() => ({ data: [] })),
          testimonialsAPI
            .getAll({ featured_only: false })
            .catch(() => ({ data: [] })),
        ]);
        setNews(newsRes.data.slice(0, 4));
        setTestimonials(testimonialsRes.data.slice(0, 4));
      } catch (error) {
        console.error("Failed to load public data:", error);
      }
    };
    loadPublicData();
  }, []);

  useEffect(() => {
    const BASE_URL =
      process.env.REACT_APP_BACKEND_URL;

    fetch(`${BASE_URL}/api/public/services-menu`)
      .then((res) => res.json())
      .then((data) => {
        setMenuData(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Failed to load services menu", err);
        setMenuData([]);
      });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
          }
        });
      },
      { threshold: 0.1 },
    );

    const statsSection = document.getElementById("stats-section");
    if (statsSection) {
      observer.observe(statsSection);
    }

    return () => {
      if (statsSection) {
        observer.unobserve(statsSection);
      }
    };
  }, []);

  const loadCenters = async () => {
    try {
      const response = await centersAPI.getAll();
      setCenters(response.data);
    } catch (error) {
      console.error("Failed to load centers:", error);
    }
  };

  const handleBookAppointmentClick = () => {
    setShowCenterSelection(true);
    loadCenters();
  };

  const handleCenterSelect = (centerId) => {
    const center = centers.find((c) => c.id === centerId);
    setSelectedCenter(center);
    setShowCenterSelection(false);
    setShowCalendar(true);
  };

  const handleSlotSelect = (slot) => {
    navigate("/appointments/booking-form", {
      state: {
        center: selectedCenter,
        slot: slot,
      },
    });
  };

  const handleBackToCenter = () => {
    setShowCalendar(false);
    setShowCenterSelection(true);
    setSelectedCenter(null);
  };

  const handleBackToHome = () => {
    setShowCalendar(false);
    setShowCenterSelection(false);
    setSelectedCenter(null);
  };

  const services = [
    {
      icon: Calendar,
      title: t("appointmentService"),
      description: t("appointmentDesc"),
      image:
        "https://images.unsplash.com/photo-1639034741369-1e0c771adaeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxwYXNzcG9ydCUyMHZpc2ElMjB0cmF2ZWwlMjBkb2N1bWVudHMlMjBmbGF0JTIwbGF5JTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc2NDc1OTQxNnww&ixlib=rb-4.1.0&q=85",
      action: handleBookAppointmentClick,
    },
    {
      icon: FileText,
      title: t("grievanceService"),
      description: t("grievanceDesc"),
      image:
        "https://images.pexels.com/photos/7689659/pexels-photo-7689659.jpeg",
      action: () => navigate("/grievances"),
    },
    {
      icon: HeadphonesIcon,
      title: t("supportService"),
      description: t("supportDesc"),
      image:
        "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=500",
      action: () => { },
    },
  ];

  const stats = [
    { label: t("totalAppointments"), value: 12500 },
    { label: t("activeCenters"), value: 15 },
    { label: t("satisfiedUsers"), value: 98 },
    { label: t("servicesProvided"), value: 45 },
  ];

  const faqs = [
    {
      question: t("faq1Q"),
      answer: t("faq1A"),
    },
    {
      question: t("faq2Q"),
      answer: t("faq2A"),
    },
    {
      question: t("faq3Q"),
      answer: t("faq3A"),
    },
    {
      question: t("faq4Q"),
      answer: t("faq4A"),
    },
    {
      question: t("faq5Q"),
      answer: t("faq5A"),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Enhanced Hero Section */}
      <section
        className={`relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 ${showCalendar || showCenterSelection
            ? "min-h-0 py-8"
            : "h-[50vh] min-h-[400px]"
          }`}
        data-testid="hero-section"
      >
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1759553497445-3b4107a905ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnb3Zlcm5tZW50JTIwb2ZmaWNlJTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMHN0ZWVsfGVufDB8fHx8MTc2NDc1OTQxNXww&ixlib=rb-4.1.0&q=85')`

            }}
          />
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-transparent to-slate-900/50" />

        {/* Floating Orbs */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-20 left-20 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          className="absolute bottom-20 right-20 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl"
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <AnimatePresence mode="wait">
            {/* Initial Hero Content */}
            {!showCenterSelection && !showCalendar && (
              <motion.div
                key="hero-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                {/* Main Heading */}
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2 leading-tight"
                >
                  {t("heroTitle")}
                </motion.h1>

                {/* Subheading */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-base sm:text-2xl text-white/80 mb-5 max-w-2xl mx-auto"
                >
                  {t("heroSubtitle")}
                </motion.p>

                {/* CTA Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Button
                    size="lg"
                    onClick={handleBookAppointmentClick}
                    className="group relative h-12 px-8 bg-[#c7e6f9] text-black font-semibold rounded-full hover:bg-[#c7e6f9] hover:text-black"
                    data-testid="hero-book-appointment-btn"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    {t("bookAppointment")}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </motion.div>
              </motion.div>
            )}

            {/* Center Selection */}
            {showCenterSelection && !showCalendar && (
              <motion.div
                key="center-selection"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                className="w-full"
              >
                <div className="max-w-5xl mx-auto">
                  {/* Header */}
                  <div className="text-center mb-6">
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="inline-flex items-center gap-2 px-3 py-1.5 mb-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full"
                    ></motion.div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                      Select Your Service Center
                    </h2>
                    <p className="text-white/70 text-sm sm:text-base">
                      Choose your preferred ICAC location to continue
                    </p>
                  </div>

                  {/* Center Cards Grid */}
                  <div className="flex justify-center">
                    <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(260px,1fr))] w-full max-w-4xl">
                      {centers.map((center, index) => (
                        <motion.button
                          key={center.id}
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ y: -5, scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleCenterSelect(center.id)}
                          className="group relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-4 text-left hover:bg-white/15 hover:border-white/30 transition-all"
                        >
                          {/* Icon */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#c7e6f9] to-[#a8d8f3] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                              <Building2 className="w-5 h-5 text-black" />
                            </div>
                            <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#c7e6f9] group-hover:translate-x-1 transition-all" />
                          </div>

                          {/* Content */}
                          <h3 className="text-base font-bold text-white mb-1 group-hover:text-[#c7e6f9] transition-colors">
                            {center.name}
                          </h3>
                          <p className="text-white/60 text-xs flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {center.city}
                          </p>

                          {/* Hover Effect */}
                          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#c7e6f9]/0 to-[#a8d8f3]/0 group-hover:from-[#c7e6f9]/10 group-hover:to-[#a8d8f3]/10 transition-all pointer-events-none" />
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Back Button */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mt-6 text-center"
                  >
                    <Button
                      onClick={handleBackToHome}
                      variant="outline"
                      size="sm"
                      className="bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 rounded-full text-sm"
                    >
                      Back to Home
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            )}

            {/* Calendar View */}
            {showCalendar && selectedCenter && (
              <motion.div
                key="calendar-view"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                className="w-full"
              >
                <div className="max-w-6xl mx-auto">
                  <CalendarView
                    center={selectedCenter}
                    onSelectSlot={handleSlotSelect}
                    onBack={handleBackToCenter}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Our Services Section */}
      <section className="py-12 md:py-15 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 -left-20 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-20 -right-20 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold bg-[#162b56] bg-clip-text text-transparent mb-2">
              {t("ourServices")}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-3">
              Discover our comprehensive range of services designed to meet your
              needs
            </p>
            <div className="w-24 h-1 bg-accent mx-auto" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {menuData.slice(0, 4).map((group, index) => {
              const firstServiceId = group.services?.[0]?.id;

              return (
                <div
                  key={group.id}
                  // onClick={() =>
                  //   firstServiceId && navigate(`/services/${firstServiceId}`)
                  // }
                  // Homepage card click
                  onClick={() => navigate(`/services/group/${group.id}`, { state: { group } })}
                  className={`
      group relative h-79 rounded-2xl overflow-hidden cursor-pointer 
      transform transition-all duration-500 ease-out
      hover:scale-105 hover:-translate-y-2
      opacity-0 animate-[cardPopIn_0.6s_ease-out_forwards]
    `}
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <img
                    src={SERVICE_IMAGES[group.name]}
                    alt={group.name}
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:rotate-2"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                  <div className="relative z-10 h-full flex items-end pt-12">
                    <div className="space-y-1 my-7">
                      <h3 className="text-xl text-white font-semibold transform transition-all duration-500 group-hover:translate-x-1 pl-4">
                        {group.name}
                      </h3>
                      {/* <div className="w-0 h-1 bg-white rounded-full group-hover:w-full transition-all duration-700 ease-out"></div> */}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes cardPopIn {
          0% {
            opacity: 0;
            transform: translateY(50px) scale(0.9);
          }
          60% {
            transform: translateY(-10px) scale(1.02);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>

      {/* How To Apply Section */}
      <section className="py-12 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold bg-[#162b56] bg-clip-text text-transparent mb-2">
              How to Apply at a Glance
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-3">
              A guided step-by-step process to ensure a smooth and compliant
              application journey
            </p>
            <div className="w-24 h-1 bg-accent mx-auto" />
          </div>

          <div className="hidden lg:block">
            <div className="flex items-start justify-center gap-0">
              {[
                {
                  num: "01",
                  title: "Register Online",
                  desc: "Create your account on the ICAC portal.",
                  gradient: "from-teal-400 to-teal-600",
                  bg: "from-teal-50 to-white",
                  border: "border-teal-100",
                },
                {
                  num: "02",
                  title: "Read Instructions",
                  desc: "Review application guidelines carefully.",
                  gradient: "from-blue-400 to-blue-600",
                  bg: "from-blue-50 to-white",
                  border: "border-blue-100",
                },
                {
                  num: "03",
                  title: "Fill Application",
                  desc: "Complete the form as per government norms.",
                  gradient: "from-orange-400 to-orange-600",
                  bg: "from-orange-50 to-white",
                  border: "border-orange-100",
                },
                {
                  num: "04",
                  title: "Submit & Pay",
                  desc: "Upload documents and pay applicable fees.",
                  gradient: "from-cyan-400 to-cyan-600",
                  bg: "from-cyan-50 to-white",
                  border: "border-cyan-100",
                },
                {
                  num: "05",
                  title: "Schedule Appointment",
                  desc: "Choose a convenient date and time.",
                  gradient: "from-emerald-400 to-emerald-600",
                  bg: "from-emerald-50 to-white",
                  border: "border-emerald-100",
                },
                {
                  num: "06",
                  title: "Visit ICAC",
                  desc: "Submit originals and biometrics if required.",
                  gradient: "from-indigo-400 to-indigo-600",
                  bg: "from-indigo-50 to-white",
                  border: "border-indigo-100",
                },
                {
                  num: "07",
                  title: "Track & Collect",
                  desc: "Track status and collect processed documents.",
                  gradient: "from-rose-400 to-rose-600",
                  bg: "from-rose-50 to-white",
                  border: "border-rose-100",
                },
              ].map((step, index) => (
                <React.Fragment key={index}>
                  <div
                    className="flex flex-col items-center opacity-0 animate-[flowIn_0.6s_ease-out_forwards]"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div
                      className={`relative w-20 h-20 rounded-full bg-gradient-to-br ${step.gradient} flex items-center justify-center text-white text-xl font-bold shadow-lg mb-4 transition-all duration-400 hover:scale-110 hover:shadow-2xl group z-10`}
                    >
                      <span className="relative z-10">{step.num}</span>
                      <div className="absolute inset-0 rounded-full bg-white opacity-20"></div>
                    </div>
                    <div
                      className={`bg-gradient-to-br ${step.bg} rounded-xl p-4 shadow-md border-2 ${step.border} w-36 h-36 transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}
                    >
                      <h3 className="text-sm font-bold text-slate-800 mb-2 text-center">
                        {step.title}
                      </h3>
                      <p className="text-xs text-slate-600 text-center leading-tight">
                        {step.desc}
                      </p>
                    </div>
                  </div>

                  {index < 6 && (
                    <div className="flex items-start pt-8 px-3">
                      <div className="flex flex-col items-center">
                        <div
                          className="h-1 w-16 rounded-full bg-gradient-to-r from-teal-300 via-blue-300 to-blue-500 opacity-0 animate-[lineGrow_0.8s_ease-out_forwards]"
                          style={{ animationDelay: `${0.5 + index * 0.2}s` }}
                        ></div>
                        <svg
                          className="w-6 h-6 text-blue-500 mt-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="lg:hidden space-y-4">
            {[
              {
                num: "01",
                title: "Register Online",
                desc: "Create your account on the ICAC portal.",
                gradient: "from-teal-400 to-teal-600",
                bg: "from-teal-50 to-white",
                border: "border-teal-100",
              },
              {
                num: "02",
                title: "Read Instructions",
                desc: "Review application guidelines carefully.",
                gradient: "from-blue-400 to-blue-600",
                bg: "from-blue-50 to-white",
                border: "border-blue-100",
              },
              {
                num: "03",
                title: "Fill Application",
                desc: "Complete the form as per government norms.",
                gradient: "from-orange-400 to-orange-600",
                bg: "from-orange-50 to-white",
                border: "border-orange-100",
              },
              {
                num: "04",
                title: "Submit & Pay",
                desc: "Upload documents and pay applicable fees.",
                gradient: "from-cyan-400 to-cyan-600",
                bg: "from-cyan-50 to-white",
                border: "border-cyan-100",
              },
              {
                num: "05",
                title: "Schedule Appointment",
                desc: "Choose a convenient date and time.",
                gradient: "from-emerald-400 to-emerald-600",
                bg: "from-emerald-50 to-white",
                border: "border-emerald-100",
              },
              {
                num: "06",
                title: "Visit ICAC",
                desc: "Submit originals and biometrics if required.",
                gradient: "from-indigo-400 to-indigo-600",
                bg: "from-indigo-50 to-white",
                border: "border-indigo-100",
              },
              {
                num: "07",
                title: "Track & Collect",
                desc: "Track status and collect processed documents.",
                gradient: "from-rose-400 to-rose-600",
                bg: "from-rose-50 to-white",
                border: "border-rose-100",
              },
            ].map((item, index) => (
              <div
                key={index}
                className={`flex gap-4 bg-gradient-to-br ${item.bg} border-2 ${item.border} rounded-xl p-5 shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}
              >
                <div
                  className={`relative w-14 h-14 rounded-full bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow-lg`}
                >
                  <span className="relative z-10">{item.num}</span>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 mb-1 text-base">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      <style jsx>{`
        @keyframes flowIn {
          0% {
            opacity: 0;
            transform: translateX(-80px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes lineGrow {
          from {
            width: 0;
          }
          to {
            width: 100%;
          }
        }
      `}</style>

      {/* Our Impact Section */}
      <section id="stats-section" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto mb-16 text-left">
            <p className="text-accent uppercase tracking-wide font-semibold mb-3">
              DU Digital Global
            </p>
            <h2 className="text-3xl md:text-4xl font-bold bg-[#162b56] bg-clip-text text-transparent mb-2">
              Your Trusted Partner
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mb-3">
              For Indian Passports, Visas, and other Consular Services.
            </p>
            <div className="w-24 h-1 bg-accent" />
            <p className="text-muted-foreground leading-relaxed my-4">
              Started in 2015, DU Digital Global Limited is a leading Indian
              company providing specialized outsourcing solutions for visa
              processing and consular services to governments and diplomatic
              missions worldwide, backed by technology and service excellence.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Rooted in India's innovation-driven ecosystem, we combine global
              mobility expertise with international standards. Our expanding
              portfolio reflects strong collaborations with embassies and
              consulates worldwide.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              DU Digital Global operates at the front-end of non-judgmental
              administrative services, assisting applicants through
              standardized, transparent, and compliant processes.
            </p>
            <Button className="bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-md">
              View More
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            <div className="bg-secondary border border-slate-200 rounded-xl py-6">
              <div className="text-3xl font-bold text-accent mb-1">2015</div>
              <div className="text-sm text-muted-foreground">Established</div>
            </div>
            <div className="bg-secondary border border-slate-200 rounded-xl py-6">
              <div className="text-3xl font-bold text-accent mb-1">
                {inView && <CountUp end={6} duration={3} />}
              </div>
              <div className="text-sm text-muted-foreground">Countries</div>
            </div>
            <div className="bg-secondary border border-slate-200 rounded-xl py-6">
              <div className="text-3xl font-bold text-accent mb-1">
                {inView && <CountUp end={35} duration={2} />}
              </div>
              <div className="text-sm text-muted-foreground">Locations</div>
            </div>
            <div className="bg-secondary border border-slate-200 rounded-xl py-6">
              <div className="text-3xl font-bold text-accent mb-1">
                {inView && <CountUp end={1700000} duration={2.5} suffix="+" />}
              </div>
              <div className="text-sm text-muted-foreground">
                Applications Processed
              </div>
            </div>
            <div className="bg-secondary border border-slate-200 rounded-xl py-6">
              <div className="text-3xl font-bold text-accent mb-1">
                {inView && <CountUp end={170} duration={2.5} suffix="+" />}
              </div>
              <div className="text-sm text-muted-foreground">Employees</div>
            </div>
          </div>
        </div>
      </section>

      {/* News & Media Section */}
      {news.length > 0 && (
        <section className="py-12 md:py-12 bg-white" data-testid="news-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                News & Media
              </h2>
              <div className="w-24 h-1 bg-accent mx-auto" />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {news.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                  data-testid={`news-card-${index}`}
                >
                  {item.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${item.category === "news" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}
                      >
                        {item.category}
                      </span>
                      {item.is_featured && (
                        <span className="text-xs px-2 py-1 rounded-full bg-accent/10 text-accent font-medium">
                          Featured
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-primary mb-2 line-clamp-2 group-hover:text-accent transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {item.summary || item.content}
                    </p>
                    <motion.div
                      initial={{ x: -10, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      viewport={{ once: true }}
                      className="mt-4 flex items-center text-accent text-sm font-medium cursor-pointer group-hover:gap-2 transition-all"
                    >
                      Read More <ArrowRight className="h-4 w-4 ml-1" />
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section
          className="py-12 md:py-12 bg-gradient-to-br from-primary to-primary/90 text-white"
          data-testid="testimonials-section"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                What Our Clients Say
              </h2>
              <div className="w-24 h-1 bg-accent mx-auto" />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, scale: 0.9, y: 30 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.15,
                    type: "spring",
                    stiffness: 100,
                  }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                  data-testid={`testimonial-card-${index}`}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ delay: 0.3 + index * 0.1, type: "spring" }}
                    viewport={{ once: true }}
                    className="flex gap-1 mb-4"
                  >
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${i < testimonial.rating ? "fill-amber-400 text-amber-400" : "text-white/30"}`}
                      />
                    ))}
                  </motion.div>

                  <p className="text-white/90 italic mb-6 text-sm leading-relaxed">
                    &ldquo;{testimonial.content}&rdquo;
                  </p>

                  <div className="flex items-center gap-3">
                    {testimonial.image_url ? (
                      <img
                        src={testimonial.image_url}
                        alt={testimonial.name}
                        className="w-10 h-10 rounded-full object-cover border-2 border-white/20"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.name)}&background=random`;
                        }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center border-2 border-white/20">
                        <span className="text-accent font-semibold text-sm">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-white text-sm">
                        {testimonial.name}
                      </h4>
                      <p className="text-white/60 text-xs">
                        {testimonial.designation}
                        {testimonial.company ? ` • ${testimonial.company}` : ""}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Home;

// --------End---------------

// Inder

// import React, { useEffect, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { useTranslation } from 'react-i18next';
// import { motion, AnimatePresence } from 'framer-motion';
// import CountUp from 'react-countup';
// import { Calendar, FileText, HeadphonesIcon, ChevronDown, Star, ArrowRight, Newspaper, ChevronLeft, ChevronRight, MapPin, Clock, Phone } from 'lucide-react';
// import Header from '../components/Header';
// import Footer from '../components/Footer';
// import { Button } from '@/components/ui/button';

// import {
//   Accordion,
//   AccordionContent,
//   AccordionItem,
//   AccordionTrigger,
// } from '@/components/ui/accordion';

// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from '@/components/ui/select';
// import CalendarView from '../components/booking/CalendarView';
// import { centersAPI, newsAPI, testimonialsAPI } from '../utils/api';

// const Home = () => {
//   const { t } = useTranslation();
//   const navigate = useNavigate();
//   const [inView, setInView] = useState(false);
//   const [showCenterSelection, setShowCenterSelection] = useState(false);
//   const [centers, setCenters] = useState([]);
//   const [selectedCenter, setSelectedCenter] = useState(null);
//   const [showCalendar, setShowCalendar] = useState(false);
//   const [news, setNews] = useState([]);
//   const [testimonials, setTestimonials] = useState([]);
//   const [menuData, setMenuData] = useState([]);
//   const [currentNewsSlide, setCurrentNewsSlide] = useState(0);
//   const [currentTestimonialSlide, setCurrentTestimonialSlide] = useState(0);
//   const [scrollY, setScrollY] = useState(0);

//   const SERVICE_IMAGES = {
//     "Visa Services":
//       "https://images.unsplash.com/photo-1526772662000-3f88f10405ff?q=80&w=1200",
//     "Passport Services":
//       "https://images.unsplash.com/photo-1529070538774-1843cb3265df?q=80&w=1200",
//     "Document Authentication":
//       "https://images.unsplash.com/photo-1589820296156-2454bb8a6ad1?q=80&w=1200",
//     "OCI Service":
//       "https://images.unsplash.com/photo-1546410531-bb4caa6b424d?q=80&w=1200",
//   };

//   // Parallax scroll effect
//   useEffect(() => {
//     const handleScroll = () => setScrollY(window.scrollY);
//     window.addEventListener('scroll', handleScroll);
//     return () => window.removeEventListener('scroll', handleScroll);
//   }, []);

//   useEffect(() => {
//     const loadPublicData = async () => {
//       try {
//         const [newsRes, testimonialsRes] = await Promise.all([
//           newsAPI.getAll({ featured_only: false }).catch(() => ({ data: [] })),
//           testimonialsAPI.getAll({ featured_only: false }).catch(() => ({ data: [] }))
//         ]);
//         setNews(newsRes.data.slice(0, 8));
//         setTestimonials(testimonialsRes.data.slice(0, 8));
//       } catch (error) {
//         console.error('Failed to load public data:', error);
//       }
//     };
//     loadPublicData();
//   }, []);

//   useEffect(() => {
//     const BASE_URL =
//       process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

//     fetch(`${BASE_URL}/api/public/services-menu`)
//       .then(res => res.json())
//       .then(data => {
//         setMenuData(Array.isArray(data) ? data : []);
//       })
//       .catch(err => {
//         console.error('Failed to load services menu', err);
//         setMenuData([]);
//       });
//   }, []);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       (entries) => {
//         entries.forEach((entry) => {
//           if (entry.isIntersecting) {
//             setInView(true);
//           }
//         });
//       },
//       { threshold: 0.1 }
//     );

//     const statsSection = document.getElementById('stats-section');
//     if (statsSection) {
//       observer.observe(statsSection);
//     }

//     return () => {
//       if (statsSection) {
//         observer.unobserve(statsSection);
//       }
//     };
//   }, []);

//   // Auto-play carousel for news
//   useEffect(() => {
//     if (news.length > 4) {
//       const timer = setInterval(() => {
//         setCurrentNewsSlide((prev) => (prev + 1) % Math.ceil(news.length / 4));
//       }, 5000);
//       return () => clearInterval(timer);
//     }
//   }, [news.length]);

//   // Auto-play carousel for testimonials
//   useEffect(() => {
//     if (testimonials.length > 4) {
//       const timer = setInterval(() => {
//         setCurrentTestimonialSlide((prev) => (prev + 1) % Math.ceil(testimonials.length / 4));
//       }, 5000);
//       return () => clearInterval(timer);
//     }
//   }, [testimonials.length]);

//   const loadCenters = async () => {
//     try {
//       const response = await centersAPI.getAll();
//       setCenters(response.data);
//     } catch (error) {
//       console.error('Failed to load centers:', error);
//     }
//   };

//   const handleBookAppointmentClick = () => {
//     setShowCenterSelection(true);
//     loadCenters();
//   };

//   const handleCenterSelect = (centerId) => {
//     const center = centers.find(c => c.id === centerId);
//     setSelectedCenter(center);
//     setShowCenterSelection(false);
//     setShowCalendar(true);
//   };

//   const handleSlotSelect = (slot) => {
//     navigate('/appointments/booking-form', {
//       state: {
//         center: selectedCenter,
//         slot: slot
//       }
//     });
//   };

//   const handleBackToCenter = () => {
//     setShowCalendar(false);
//     setShowCenterSelection(true);
//     setSelectedCenter(null);
//   };

//   const nextNewsSlide = () => {
//     setCurrentNewsSlide((prev) => (prev + 1) % Math.ceil(news.length / 4));
//   };

//   const prevNewsSlide = () => {
//     setCurrentNewsSlide((prev) => (prev - 1 + Math.ceil(news.length / 4)) % Math.ceil(news.length / 4));
//   };

//   const nextTestimonialSlide = () => {
//     setCurrentTestimonialSlide((prev) => (prev + 1) % Math.ceil(testimonials.length / 4));
//   };

//   const prevTestimonialSlide = () => {
//     setCurrentTestimonialSlide((prev) => (prev - 1 + Math.ceil(testimonials.length / 4)) % Math.ceil(testimonials.length / 4));
//   };

//   const services = [
//     {
//       icon: Calendar,
//       title: t('appointmentService'),
//       description: t('appointmentDesc'),
//       image: 'https://images.unsplash.com/photo-1639034741369-1e0c771adaeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxwYXNzcG9ydCUyMHZpc2ElMjB0cmF2ZWwlMjBkb2N1bWVudHMlMjBmbGF0JTIwbGF5JTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc2NDc1OTQxNnww&ixlib=rb-4.1.0&q=85',
//       action: handleBookAppointmentClick
//     },
//     {
//       icon: FileText,
//       title: t('grievanceService'),
//       description: t('grievanceDesc'),
//       image: 'https://images.pexels.com/photos/7689659/pexels-photo-7689659.jpeg',
//       action: () => navigate('/grievances')
//     },
//     {
//       icon: HeadphonesIcon,
//       title: t('supportService'),
//       description: t('supportDesc'),
//       image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=500',
//       action: () => {}
//     }
//   ];

//   const stats = [
//     { label: t('totalAppointments'), value: 12500 },
//     { label: t('activeCenters'), value: 15 },
//     { label: t('satisfiedUsers'), value: 98 },
//     { label: t('servicesProvided'), value: 45 }
//   ];

//   const faqs = [
//     {
//       question: t('faq1Q'),
//       answer: t('faq1A')
//     },
//     {
//       question: t('faq2Q'),
//       answer: t('faq2A')
//     },
//     {
//       question: t('faq3Q'),
//       answer: t('faq3A')
//     },
//     {
//       question: t('faq4Q'),
//       answer: t('faq4A')
//     },
//     {
//       question: t('faq5Q'),
//       answer: t('faq5A')
//     }
//   ];

//   return (
//     <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50">
//       <Header />

//       {/* Modern Hero Section with Parallax */}
//       <section
//         className="hero-section relative min-h-[90vh] flex items-center justify-center overflow-hidden"
//         style={{
//           backgroundImage: `url('https://images.unsplash.com/photo-1759553497445-3b4107a905ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnb3Zlcm5tZW50JTIwb2ZmaWNlJTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMHN0ZWVsfGVufDB8fHx8MTc2NDc1OTQxNXww&ixlib=rb-4.1.0&q=85')`,
//           backgroundSize: 'cover',
//           backgroundPosition: 'center',
//           backgroundAttachment: 'fixed',
//         }}
//         data-testid="hero-section"
//       >
//         {/* Animated gradient overlay */}
//         <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/90 via-purple-900/85 to-pink-900/90 animate-gradient-shift" />

//         {/* Floating particles */}
//         <div className="absolute inset-0 overflow-hidden">
//           {[...Array(20)].map((_, i) => (
//             <motion.div
//               key={i}
//               className="absolute w-2 h-2 bg-white/20 rounded-full"
//               initial={{
//                 x: Math.random() * window.innerWidth,
//                 y: Math.random() * window.innerHeight,
//               }}
//               animate={{
//                 y: [null, Math.random() * window.innerHeight],
//                 x: [null, Math.random() * window.innerWidth],
//               }}
//               transition={{
//                 duration: Math.random() * 10 + 10,
//                 repeat: Infinity,
//                 ease: "linear"
//               }}
//             />
//           ))}
//         </div>

//         <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
//           <AnimatePresence mode="wait">
//             {!showCenterSelection && !showCalendar && (
//               <motion.div
//                 key="hero-content"
//                 initial={{ opacity: 0, y: 30 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: -30 }}
//                 transition={{ duration: 0.8 }}
//                 className="text-center"
//               >
//                 <motion.div
//                   initial={{ scale: 0.9, opacity: 0 }}
//                   animate={{ scale: 1, opacity: 1 }}
//                   transition={{ delay: 0.2, duration: 0.8 }}
//                   className="inline-block mb-6"
//                 >
//                   <span className="px-6 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white text-sm font-medium">
//                     🌟 Your Gateway to Consular Services
//                   </span>
//                 </motion.div>

//                 <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
//                   {t('heroTitle')}
//                 </h1>

//                 <motion.p
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.4 }}
//                   className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed"
//                 >
//                   {t('heroSubtitle')}
//                 </motion.p>

//                 <motion.div
//                   initial={{ opacity: 0, y: 20 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.6 }}
//                   className="flex flex-col sm:flex-row gap-4 justify-center items-center"
//                 >
//                   <Button
//                     size="lg"
//                     onClick={handleBookAppointmentClick}
//                     className="group bg-white text-indigo-600 hover:bg-white/90 rounded-full h-16 px-10 text-lg font-semibold transition-all hover:scale-105 hover:shadow-2xl shadow-xl"
//                     data-testid="hero-book-appointment-btn"
//                   >
//                     {t('bookAppointment')}
//                     <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
//                   </Button>

//                   <Button
//                     size="lg"
//                     variant="outline"
//                     className="bg-white/10 backdrop-blur-md border-2 border-white/30 text-white hover:bg-white/20 rounded-full h-16 px-10 text-lg font-semibold transition-all hover:scale-105"
//                   >
//                     Learn More
//                   </Button>
//                 </motion.div>

//                 {/* Floating stats */}
//                 <motion.div
//                   initial={{ opacity: 0, y: 40 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.8 }}
//                   className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto"
//                 >
//                   {[
//                     { value: "1.7M+", label: "Applications" },
//                     { value: "35+", label: "Locations" },
//                     { value: "6", label: "Countries" },
//                     { value: "98%", label: "Satisfaction" }
//                   ].map((stat, index) => (
//                     <motion.div
//                       key={index}
//                       whileHover={{ scale: 1.05, y: -5 }}
//                       className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-white"
//                     >
//                       <div className="text-3xl font-bold mb-1">{stat.value}</div>
//                       <div className="text-sm text-white/70">{stat.label}</div>
//                     </motion.div>
//                   ))}
//                 </motion.div>
//               </motion.div>
//             )}

//             {/* Center Selection with Glass Effect */}
//             {showCenterSelection && (
//               <motion.div
//                 key="center-selection"
//                 initial={{ opacity: 0, scale: 0.95 }}
//                 animate={{ opacity: 1, scale: 1 }}
//                 exit={{ opacity: 0, scale: 0.95 }}
//                 transition={{ duration: 0.6 }}
//                 className="max-w-6xl mx-auto"
//               >
//                 <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-2xl p-10">

//                   {/* Animated background glow */}
//                   <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 blur-3xl opacity-50 animate-pulse" />

//                   <div className="relative z-10">
//                     {/* Heading */}
//                     <motion.div
//                       initial={{ y: -20, opacity: 0 }}
//                       animate={{ y: 0, opacity: 1 }}
//                       className="text-center mb-10"
//                     >
//                       <h2 className="text-4xl font-bold text-white mb-3">
//                         Select Service Center
//                       </h2>
//                       <p className="text-white/70 text-lg">
//                         Choose your preferred ICAC location to proceed
//                       </p>
//                       <div className="mt-4 flex justify-center gap-2">
//                         <div className="h-1 w-16 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full" />
//                         <div className="h-1 w-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full" />
//                       </div>
//                     </motion.div>

//                     {/* Center Cards Grid */}
//                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
//                       {centers.map((center, index) => (
//                         <motion.button
//                           key={center.id}
//                           initial={{ opacity: 0, y: 30 }}
//                           animate={{ opacity: 1, y: 0 }}
//                           transition={{ delay: index * 0.1 }}
//                           whileHover={{ y: -8, scale: 1.02 }}
//                           whileTap={{ scale: 0.98 }}
//                           onClick={() => handleCenterSelect(center.id)}
//                           className="group text-left relative overflow-hidden"
//                         >
//                           {/* Glass card */}
//                           <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 transition-all duration-300 group-hover:bg-white/20 group-hover:border-white/30 group-hover:shadow-2xl">

//                             {/* Gradient overlay on hover */}
//                             <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-purple-500/0 to-pink-500/0 group-hover:from-blue-500/10 group-hover:via-purple-500/10 group-hover:to-pink-500/10 rounded-2xl transition-all duration-300" />

//                             <div className="relative z-10">
//                               {/* Icon */}
//                               <div className="w-14 h-14 mb-4 rounded-xl bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-xl shadow-lg group-hover:scale-110 transition-transform">
//                                 <MapPin className="w-7 h-7" />
//                               </div>

//                               {/* Center Name */}
//                               <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-200 transition-colors">
//                                 {center.name}
//                               </h3>

//                               {/* City */}
//                               <p className="text-white/60 text-sm mb-4 flex items-center gap-2">
//                                 <MapPin className="w-4 h-4" />
//                                 {center.city}
//                               </p>

//                               {/* CTA */}
//                               <div className="flex items-center gap-2 text-blue-300 font-medium text-sm group-hover:gap-3 transition-all">
//                                 Select Center
//                                 <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
//                               </div>
//                             </div>

//                             {/* Shine effect */}
//                             <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
//                               <div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 animate-shine" />
//                             </div>
//                           </div>
//                         </motion.button>
//                       ))}
//                     </div>

//                     {/* Back button */}
//                     <motion.div
//                       initial={{ opacity: 0 }}
//                       animate={{ opacity: 1 }}
//                       transition={{ delay: 0.5 }}
//                       className="mt-8 text-center"
//                     >
//                       <Button
//                         onClick={() => setShowCenterSelection(false)}
//                         variant="outline"
//                         className="bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 rounded-full px-8"
//                       >
//                         <ChevronLeft className="w-4 h-4 mr-2" />
//                         Back to Home
//                       </Button>
//                     </motion.div>
//                   </div>
//                 </div>
//               </motion.div>
//             )}

//             {showCalendar && selectedCenter && (
//               <motion.div
//                 key="calendar-view"
//                 initial={{ opacity: 0, scale: 0.95 }}
//                 animate={{ opacity: 1, scale: 1 }}
//                 exit={{ opacity: 0, scale: 0.95 }}
//                 transition={{ duration: 0.5 }}
//                 className="max-w-6xl mx-auto"
//               >
//                 <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-2xl p-8">
//                   <CalendarView
//                     center={selectedCenter}
//                     onSelectSlot={handleSlotSelect}
//                     onBack={handleBackToCenter}
//                   />
//                 </div>
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </div>
//       </section>

//       {/* Our Services Section - Enhanced */}
//       <section className="py-24 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 relative overflow-hidden">

//         {/* Animated background elements */}
//         <div className="absolute inset-0 overflow-hidden pointer-events-none">
//           <div className="absolute top-20 -left-20 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-float" />
//           <div className="absolute bottom-20 -right-20 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-float-delayed" />
//           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-400/10 rounded-full blur-3xl animate-pulse" />
//         </div>

//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

//           {/* Header */}
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             viewport={{ once: true }}
//             transition={{ duration: 0.6 }}
//             className="text-center mb-16"
//           >
//             <motion.span
//               initial={{ opacity: 0, scale: 0.8 }}
//               whileInView={{ opacity: 1, scale: 1 }}
//               viewport={{ once: true }}
//               className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-indigo-600 rounded-full text-sm font-semibold mb-4"
//             >
//               What We Offer
//             </motion.span>

//             <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
//               {t("ourServices")}
//             </h2>

//             <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6">
//               Comprehensive consular services designed to meet all your needs with excellence
//             </p>

//             <div className="flex justify-center gap-2">
//               <motion.div
//                 initial={{ width: 0 }}
//                 whileInView={{ width: 80 }}
//                 viewport={{ once: true }}
//                 className="h-1.5 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
//               />
//               <motion.div
//                 initial={{ width: 0 }}
//                 whileInView={{ width: 80 }}
//                 viewport={{ once: true }}
//                 transition={{ delay: 0.1 }}
//                 className="h-1.5 bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
//               />
//               <motion.div
//                 initial={{ width: 0 }}
//                 whileInView={{ width: 80 }}
//                 viewport={{ once: true }}
//                 transition={{ delay: 0.2 }}
//                 className="h-1.5 bg-gradient-to-r from-pink-500 to-pink-400 rounded-full"
//               />
//             </div>
//           </motion.div>

//           {/* Cards Grid */}
//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
//             {menuData.slice(0, 4).map((group, index) => {
//               const firstServiceId = group.services?.[0]?.id;

//               const gradients = [
//                 'from-teal-400 via-cyan-500 to-blue-600',
//                 'from-purple-400 via-pink-500 to-rose-600',
//                 'from-orange-400 via-red-500 to-pink-600',
//                 'from-indigo-400 via-purple-500 to-pink-600'
//               ];

//               const glowColors = [
//                 'group-hover:shadow-teal-500/50',
//                 'group-hover:shadow-purple-500/50',
//                 'group-hover:shadow-orange-500/50',
//                 'group-hover:shadow-indigo-500/50'
//               ];

//               return (
//                 <motion.div
//                   key={group.id}
//                   initial={{ opacity: 0, y: 50 }}
//                   whileInView={{ opacity: 1, y: 0 }}
//                   viewport={{ once: true }}
//                   transition={{ delay: index * 0.1, duration: 0.6 }}
//                   onClick={() =>
//                     firstServiceId && navigate(`/services/${firstServiceId}`)
//                   }
//                   className="group relative h-80 rounded-3xl overflow-hidden cursor-pointer transform transition-all duration-500 hover:scale-105 hover:-translate-y-3"
//                 >
//                   {/* Background Image */}
//                   <div className="absolute inset-0">
//                     <img
//                       src={SERVICE_IMAGES[group.name]}
//                       alt={group.name}
//                       className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:rotate-2"
//                     />
//                   </div>

//                   {/* Gradient Overlay */}
//                   <div className={`absolute inset-0 bg-gradient-to-br ${gradients[index % 4]} opacity-70 group-hover:opacity-80 transition-opacity duration-500`} />

//                   {/* Dark Gradient for Text */}
//                   <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

//                   {/* Animated Border */}
//                   <div className="absolute inset-0 border-2 border-white/20 opacity-0 group-hover:opacity-100 group-hover:border-white/40 transition-all duration-500 rounded-3xl" />

//                   {/* Content */}
//                   <div className="relative z-10 h-full flex flex-col justify-between p-6">

//                     {/* Top Badge */}
//                     <div className="flex justify-end">
//                       <motion.div
//                         whileHover={{ rotate: 12, scale: 1.1 }}
//                         className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center shadow-lg"
//                       >
//                         <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
//                         </svg>
//                       </motion.div>
//                     </div>

//                     {/* Bottom Content */}
//                     <div className="space-y-4">
//                       <motion.h3
//                         className="text-2xl font-bold text-white"
//                         whileHover={{ x: 5 }}
//                       >
//                         {group.name}
//                       </motion.h3>

//                       {/* Service count badge */}
//                       <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 rounded-full px-4 py-2 text-white text-sm">
//                         <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
//                         {group.services?.length || 0} Services Available
//                       </div>

//                       {/* Explore Button */}
//                       <motion.div
//                         whileHover={{ x: 5 }}
//                         className="flex items-center gap-2 text-white font-semibold"
//                       >
//                         <span className="text-sm tracking-wide">Explore Services</span>
//                         <ArrowRight className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-2" />
//                       </motion.div>

//                       {/* Progress Bar */}
//                       <div className="h-1 bg-white/20 rounded-full overflow-hidden">
//                         <motion.div
//                           initial={{ width: 0 }}
//                           whileInView={{ width: "100%" }}
//                           viewport={{ once: true }}
//                           transition={{ delay: index * 0.2, duration: 1 }}
//                           className="h-full bg-white rounded-full"
//                         />
//                       </div>
//                     </div>
//                   </div>

//                   {/* Floating particles on hover */}
//                   <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
//                     {[...Array(5)].map((_, i) => (
//                       <motion.div
//                         key={i}
//                         className="absolute w-2 h-2 bg-white rounded-full"
//                         initial={{
//                           x: `${20 + i * 20}%`,
//                           y: `${30 + i * 10}%`,
//                           scale: 0
//                         }}
//                         whileInView={{
//                           y: [null, `${10 + i * 10}%`],
//                           scale: [0, 1, 0],
//                         }}
//                         transition={{
//                           duration: 2,
//                           repeat: Infinity,
//                           delay: i * 0.2,
//                         }}
//                       />
//                     ))}
//                   </div>

//                   {/* Shine effect */}
//                   <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000">
//                     <div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 group-hover:animate-shine-slow" />
//                   </div>
//                 </motion.div>
//               );
//             })}
//           </div>

//           {/* Bottom CTA */}
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             viewport={{ once: true }}
//             className="mt-16 text-center"
//           >
//             <motion.button
//               whileHover={{ scale: 1.05 }}
//               whileTap={{ scale: 0.95 }}
//               className="group relative px-10 py-5 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white font-bold text-lg rounded-full shadow-2xl overflow-hidden"
//             >
//               <span className="relative z-10 flex items-center gap-2">
//                 View All Services
//                 <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
//               </span>
//               <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
//               <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
//                 <div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 group-hover:animate-shine-slow" />
//               </div>
//             </motion.button>
//           </motion.div>

//         </div>
//       </section>

//       {/* How To Apply Section - Enhanced */}
//       <section className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 relative overflow-hidden">

//         {/* Animated background */}
//         <div className="absolute inset-0 overflow-hidden pointer-events-none">
//           <div className="absolute top-0 left-0 w-full h-full">
//             <div className="absolute top-20 left-20 w-72 h-72 bg-blue-300/20 rounded-full blur-3xl animate-float" />
//             <div className="absolute bottom-20 right-20 w-72 h-72 bg-purple-300/20 rounded-full blur-3xl animate-float-delayed" />
//           </div>
//         </div>

//         <div className="max-w-7xl mx-auto px-6 relative z-10">

//           {/* Header */}
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             viewport={{ once: true }}
//             className="text-center mb-16"
//           >
//             <motion.span
//               initial={{ opacity: 0, scale: 0.8 }}
//               whileInView={{ opacity: 1, scale: 1 }}
//               viewport={{ once: true }}
//               className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-indigo-600 rounded-full text-sm font-semibold mb-4"
//             >
//               Simple Process
//             </motion.span>

//             <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
//               How to Apply at a Glance
//             </h2>

//             <p className="text-muted-foreground max-w-3xl mx-auto text-lg">
//               A guided step-by-step process to ensure a smooth and compliant application journey
//             </p>

//             <div className="mt-6 flex justify-center gap-2">
//               {[0, 1, 2].map((i) => (
//                 <motion.div
//                   key={i}
//                   initial={{ width: 0 }}
//                   whileInView={{ width: 80 }}
//                   viewport={{ once: true }}
//                   transition={{ delay: i * 0.1 }}
//                   className={`h-1.5 rounded-full bg-gradient-to-r ${
//                     i === 0 ? 'from-blue-500 to-blue-400' :
//                     i === 1 ? 'from-purple-500 to-purple-400' :
//                     'from-pink-500 to-pink-400'
//                   }`}
//                 />
//               ))}
//             </div>
//           </motion.div>

//           {/* Desktop Flow */}
//           <div className="hidden lg:block">
//             <div className="flex items-start justify-center gap-0">
//               {[
//                 { num: '01', title: 'Register Online', desc: 'Create your account on the ICAC portal.', gradient: 'from-teal-400 to-teal-600', bg: 'from-teal-50 to-white', border: 'border-teal-100' },
//                 { num: '02', title: 'Read Instructions', desc: 'Review application guidelines carefully.', gradient: 'from-blue-400 to-blue-600', bg: 'from-blue-50 to-white', border: 'border-blue-100' },
//                 { num: '03', title: 'Fill Application', desc: 'Complete the form as per government norms.', gradient: 'from-orange-400 to-orange-600', bg: 'from-orange-50 to-white', border: 'border-orange-100' },
//                 { num: '04', title: 'Submit & Pay', desc: 'Upload documents and pay applicable fees.', gradient: 'from-cyan-400 to-cyan-600', bg: 'from-cyan-50 to-white', border: 'border-cyan-100' },
//                 { num: '05', title: 'Schedule Appointment', desc: 'Choose a convenient date and time.', gradient: 'from-emerald-400 to-emerald-600', bg: 'from-emerald-50 to-white', border: 'border-emerald-100' },
//                 { num: '06', title: 'Visit ICAC', desc: 'Submit originals and biometrics if required.', gradient: 'from-indigo-400 to-indigo-600', bg: 'from-indigo-50 to-white', border: 'border-indigo-100' },
//                 { num: '07', title: 'Track & Collect', desc: 'Track status and collect processed documents.', gradient: 'from-rose-400 to-rose-600', bg: 'from-rose-50 to-white', border: 'border-rose-100' }
//               ].map((step, index) => (
//                 <React.Fragment key={index}>
//                   {/* Step */}
//                   <motion.div
//                     initial={{ opacity: 0, x: -100 }}
//                     whileInView={{ opacity: 1, x: 0 }}
//                     viewport={{ once: true }}
//                     transition={{ delay: index * 0.1, type: "spring", stiffness: 100 }}
//                     className="flex flex-col items-center"
//                   >
//                     <motion.div
//                       whileHover={{ scale: 1.15, rotate: 5 }}
//                       className={`relative w-20 h-20 rounded-full bg-gradient-to-br ${step.gradient} flex items-center justify-center text-white text-xl font-bold shadow-xl mb-4 z-10`}
//                     >
//                       <span className="relative z-10">{step.num}</span>
//                       <div className="absolute inset-0 rounded-full bg-white opacity-20 " />
//                     </motion.div>

//                     <motion.div
//                       whileHover={{ y: -5, scale: 1.05 }}
//                       className={`bg-gradient-to-br ${step.bg} rounded-2xl p-5 shadow-lg border-2 ${step.border} w-40 backdrop-blur-sm bg-white/80`}
//                     >
//                       <h3 className="text-sm font-bold text-slate-800 mb-2 text-center">{step.title}</h3>
//                       <p className="text-xs text-slate-600 text-center leading-tight">{step.desc}</p>
//                     </motion.div>
//                   </motion.div>

//                   {/* Arrow */}
//                   {index < 6 && (
//                     <div className="flex items-start pt-10 px-3">
//                       <div className="flex flex-col items-center">
//                         <motion.div
//                           initial={{ width: 0 }}
//                           whileInView={{ width: 64 }}
//                           viewport={{ once: true }}
//                           transition={{ delay: index * 0.1 + 0.3, duration: 0.8 }}
//                           className={`h-1 w-16 rounded-full bg-gradient-to-r ${
//                             index % 2 === 0 ? 'from-teal-300 to-blue-500' : 'from-blue-400 to-purple-500'
//                           }`}
//                         />
//                         <motion.div
//                           animate={{
//                             x: [0, 8, 0],
//                           }}
//                           transition={{
//                             duration: 1.5,
//                             repeat: Infinity,
//                             ease: "easeInOut"
//                           }}
//                         >
//                           <svg className="w-6 h-6 text-purple-500 mt-1" fill="currentColor" viewBox="0 0 20 20">
//                             <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/>
//                           </svg>
//                         </motion.div>
//                       </div>
//                     </div>
//                   )}
//                 </React.Fragment>
//               ))}
//             </div>
//           </div>

//           {/* Mobile Flow */}
//           <div className="lg:hidden space-y-4">
//             {[
//               { num: '01', title: 'Register Online', desc: 'Create your account on the ICAC portal.', gradient: 'from-teal-400 to-teal-600', bg: 'from-teal-50 to-white', border: 'border-teal-100' },
//               { num: '02', title: 'Read Instructions', desc: 'Review application guidelines carefully.', gradient: 'from-blue-400 to-blue-600', bg: 'from-blue-50 to-white', border: 'border-blue-100' },
//               { num: '03', title: 'Fill Application', desc: 'Complete the form as per government norms.', gradient: 'from-orange-400 to-orange-600', bg: 'from-orange-50 to-white', border: 'border-orange-100' },
//               { num: '04', title: 'Submit & Pay', desc: 'Upload documents and pay applicable fees.', gradient: 'from-cyan-400 to-cyan-600', bg: 'from-cyan-50 to-white', border: 'border-cyan-100' },
//               { num: '05', title: 'Schedule Appointment', desc: 'Choose a convenient date and time.', gradient: 'from-emerald-400 to-emerald-600', bg: 'from-emerald-50 to-white', border: 'border-emerald-100' },
//               { num: '06', title: 'Visit ICAC', desc: 'Submit originals and biometrics if required.', gradient: 'from-indigo-400 to-indigo-600', bg: 'from-indigo-50 to-white', border: 'border-indigo-100' },
//               { num: '07', title: 'Track & Collect', desc: 'Track status and collect processed documents.', gradient: 'from-rose-400 to-rose-600', bg: 'from-rose-50 to-white', border: 'border-rose-100' }
//             ].map((item, index) => (
//               <motion.div
//                 key={index}
//                 initial={{ opacity: 0, x: -50 }}
//                 whileInView={{ opacity: 1, x: 0 }}
//                 viewport={{ once: true }}
//                 transition={{ delay: index * 0.1 }}
//                 whileHover={{ scale: 1.02, x: 5 }}
//                 className={`flex gap-4 bg-gradient-to-br ${item.bg} border-2 ${item.border} rounded-2xl p-5 shadow-lg backdrop-blur-sm bg-white/80`}
//               >
//                 <motion.div
//                   whileHover={{ rotate: 360 }}
//                   transition={{ duration: 0.6 }}
//                   className={`relative w-14 h-14 rounded-full bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow-lg`}
//                 >
//                   <span className="relative z-10">{item.num}</span>
//                   <div className="absolute inset-0 rounded-full opacity-20 " />
//                 </motion.div>
//                 <div>
//                   <h3 className="font-bold text-slate-800 mb-1 text-base">{item.title}</h3>
//                   <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
//                 </div>
//               </motion.div>
//             ))}
//           </div>

//         </div>
//       </section>

//       {/* Our Impact Section */}
//       <section id="stats-section" className="py-24 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 text-white relative overflow-hidden">

//         {/* Animated background */}
//         <div className="absolute inset-0 overflow-hidden">
//           <div className="absolute top-0 left-0 w-full h-full opacity-30">
//             {[...Array(30)].map((_, i) => (
//               <motion.div
//                 key={i}
//                 className="absolute w-1 h-1 bg-white rounded-full"
//                 initial={{
//                   x: Math.random() * window.innerWidth,
//                   y: Math.random() * 1000,
//                 }}
//                 animate={{
//                   y: [null, -100],
//                   opacity: [0, 1, 0],
//                 }}
//                 transition={{
//                   duration: Math.random() * 3 + 2,
//                   repeat: Infinity,
//                   delay: Math.random() * 2,
//                 }}
//               />
//             ))}
//           </div>
//         </div>

//         <div className="max-w-7xl mx-auto px-6 text-center relative z-10">

//           {/* Content */}
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             viewport={{ once: true }}
//             className="max-w-4xl mx-auto mb-16"
//           >
//             <motion.span
//               initial={{ opacity: 0, scale: 0.8 }}
//               whileInView={{ opacity: 1, scale: 1 }}
//               viewport={{ once: true }}
//               className="inline-block px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-sm font-semibold mb-4"
//             >
//               DU Digital Global
//             </motion.span>

//             <h2 className="text-4xl md:text-5xl font-bold mb-6">
//               Your Trusted Partner
//             </h2>

//             <p className="text-white/80 text-lg leading-relaxed mb-6">
//               For Indian Passports, Visas, and other Consular Services.
//             </p>

//             <div className="space-y-4 text-white/70 leading-relaxed text-base">
//               <p>
//                 Started in 2015, DU Digital Global Limited is a leading Indian company
//                 providing specialized outsourcing solutions for visa processing and
//                 consular services to governments and diplomatic missions worldwide,
//                 backed by technology and service excellence.
//               </p>

//               <p>
//                 Rooted in India's innovation-driven ecosystem, we combine global mobility
//                 expertise with international standards. Our expanding portfolio reflects
//                 strong collaborations with embassies and consulates worldwide.
//               </p>

//               <p>
//                 DU Digital Global operates at the front-end of non-judgmental
//                 administrative services, assisting applicants through standardized,
//                 transparent, and compliant processes.
//               </p>
//             </div>

//             <motion.div
//               whileHover={{ scale: 1.05 }}
//               whileTap={{ scale: 0.95 }}
//               className="mt-8"
//             >
//               <Button className="bg-white text-indigo-600 hover:bg-white/90 px-8 py-3 rounded-full font-semibold shadow-xl">
//                 View More
//                 <ArrowRight className="ml-2 w-4 h-4" />
//               </Button>
//             </motion.div>
//           </motion.div>

//           {/* Metrics Cards */}
//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
//             {[
//               { value: '2015', label: 'Established', isYear: true },
//               { value: 6, label: 'Countries', duration: 3 },
//               { value: 35, label: 'Locations', duration: 2 },
//               { value: 1700000, label: 'Applications Processed', duration: 2.5, suffix: '+' },
//               { value: 170, label: 'Employees', duration: 2.5, suffix: '+' }
//             ].map((stat, index) => (
//               <motion.div
//                 key={index}
//                 initial={{ opacity: 0, y: 30 }}
//                 whileInView={{ opacity: 1, y: 0 }}
//                 viewport={{ once: true }}
//                 transition={{ delay: index * 0.1 }}
//                 whileHover={{ y: -10, scale: 1.05 }}
//                 className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl py-8 px-4 shadow-2xl"
//               >
//                 <div className="text-4xl font-bold text-white mb-2">
//                   {stat.isYear ? (
//                     stat.value
//                   ) : (
//                     inView && <CountUp end={stat.value} duration={stat.duration} suffix={stat.suffix || ''} />
//                   )}
//                 </div>
//                 <div className="text-sm text-white/70">
//                   {stat.label}
//                 </div>
//               </motion.div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* News & Media Section with Carousel */}
//       {news.length > 0 && (
//         <section className="py-24 bg-white relative overflow-hidden" data-testid="news-section">

//           {/* Background decoration */}
//           <div className="absolute inset-0 overflow-hidden pointer-events-none">
//             <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/50 rounded-full blur-3xl" />
//             <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-100/50 rounded-full blur-3xl" />
//           </div>

//           <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

//             {/* Header */}
//             <motion.div
//               initial={{ opacity: 0, y: 20 }}
//               whileInView={{ opacity: 1, y: 0 }}
//               viewport={{ once: true }}
//               className="text-center mb-12"
//             >
//               <motion.span
//                 initial={{ opacity: 0, scale: 0.8 }}
//                 whileInView={{ opacity: 1, scale: 1 }}
//                 viewport={{ once: true }}
//                 className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-indigo-600 rounded-full text-sm font-semibold mb-4"
//               >
//                 <Newspaper className="w-4 h-4" />
//                 Latest Updates
//               </motion.span>

//               <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
//                 News & Media
//               </h2>

//               <div className="w-24 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 mx-auto rounded-full" />
//             </motion.div>

//             {/* Carousel Container */}
//             <div className="relative">

//               {/* Cards */}
//               <div className="overflow-hidden">
//                 <motion.div
//                   animate={{ x: `-${currentNewsSlide * 100}%` }}
//                   transition={{ duration: 0.5, ease: "easeInOut" }}
//                   className="flex"
//                 >
//                   {Array.from({ length: Math.ceil(news.length / 4) }).map((_, slideIndex) => (
//                     <div key={slideIndex} className="min-w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
//                       {news.slice(slideIndex * 4, slideIndex * 4 + 4).map((item, index) => (
//                         <motion.div
//                           key={item.id}
//                           initial={{ opacity: 0, y: 50 }}
//                           whileInView={{ opacity: 1, y: 0 }}
//                           viewport={{ once: true }}
//                           transition={{ delay: index * 0.1 }}
//                           whileHover={{ y: -10, scale: 1.02 }}
//                           className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-100"
//                         >
//                           {item.image_url && (
//                             <div className="h-48 overflow-hidden relative">
//                               <img
//                                 src={item.image_url}
//                                 alt={item.title}
//                                 className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
//                                 onError={(e) => { e.target.style.display = 'none'; }}
//                               />
//                               <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
//                             </div>
//                           )}
//                           <div className="p-6">
//                             <div className="flex items-center gap-2 mb-3">
//                               <span className={`text-xs px-3 py-1 rounded-full font-medium ${
//                                 item.category === 'news'
//                                   ? 'bg-blue-100 text-blue-700'
//                                   : 'bg-amber-100 text-amber-700'
//                               }`}>
//                                 {item.category}
//                               </span>
//                               {item.is_featured && (
//                                 <span className="text-xs px-3 py-1 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 font-medium">
//                                   Featured
//                                 </span>
//                               )}
//                             </div>
//                             <h3 className="font-bold text-slate-800 mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors text-lg">
//                               {item.title}
//                             </h3>
//                             <p className="text-sm text-slate-600 line-clamp-2 mb-4">
//                               {item.summary || item.content}
//                             </p>
//                             <div className="flex items-center text-indigo-600 text-sm font-semibold cursor-pointer group-hover:gap-2 transition-all">
//                               Read More
//                               <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
//                             </div>
//                           </div>
//                         </motion.div>
//                       ))}
//                     </div>
//                   ))}
//                 </motion.div>
//               </div>

//               {/* Navigation Buttons */}
//               {news.length > 4 && (
//                 <>
//                   <motion.button
//                     whileHover={{ scale: 1.1 }}
//                     whileTap={{ scale: 0.9 }}
//                     onClick={prevNewsSlide}
//                     className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all z-10"
//                   >
//                     <ChevronLeft className="w-6 h-6" />
//                   </motion.button>

//                   <motion.button
//                     whileHover={{ scale: 1.1 }}
//                     whileTap={{ scale: 0.9 }}
//                     onClick={nextNewsSlide}
//                     className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all z-10"
//                   >
//                     <ChevronRight className="w-6 h-6" />
//                   </motion.button>
//                 </>
//               )}

//               {/* Dots Indicator */}
//               {news.length > 4 && (
//                 <div className="flex justify-center gap-2 mt-8">
//                   {Array.from({ length: Math.ceil(news.length / 4) }).map((_, index) => (
//                     <motion.button
//                       key={index}
//                       whileHover={{ scale: 1.2 }}
//                       onClick={() => setCurrentNewsSlide(index)}
//                       className={`h-2 rounded-full transition-all duration-300 ${
//                         currentNewsSlide === index
//                           ? 'w-8 bg-gradient-to-r from-indigo-600 to-purple-600'
//                           : 'w-2 bg-slate-300'
//                       }`}
//                     />
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>
//         </section>
//       )}

//       {/* Testimonials Section with Carousel */}
//       {testimonials.length > 0 && (
//         <section className="py-24 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 text-white relative overflow-hidden" data-testid="testimonials-section">

//           {/* Animated background */}
//           <div className="absolute inset-0 overflow-hidden pointer-events-none">
//             {[...Array(50)].map((_, i) => (
//               <motion.div
//                 key={i}
//                 className="absolute w-1 h-1 bg-white/30 rounded-full"
//                 initial={{
//                   x: Math.random() * window.innerWidth,
//                   y: Math.random() * 1000,
//                 }}
//                 animate={{
//                   y: [null, -100],
//                   opacity: [0, 1, 0],
//                 }}
//                 transition={{
//                   duration: Math.random() * 3 + 2,
//                   repeat: Infinity,
//                   delay: Math.random() * 2,
//                 }}
//               />
//             ))}
//           </div>

//           <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

//             {/* Header */}
//             <motion.div
//               initial={{ opacity: 0, y: 20 }}
//               whileInView={{ opacity: 1, y: 0 }}
//               viewport={{ once: true }}
//               className="text-center mb-12"
//             >
//               <motion.span
//                 initial={{ opacity: 0, scale: 0.8 }}
//                 whileInView={{ opacity: 1, scale: 1 }}
//                 viewport={{ once: true }}
//                 className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-sm font-semibold mb-4"
//               >
//                 <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
//                 Client Stories
//               </motion.span>

//               <h2 className="text-4xl md:text-5xl font-bold mb-4">
//                 What Our Clients Say
//               </h2>

//               <div className="w-24 h-1 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 mx-auto rounded-full" />
//             </motion.div>

//             {/* Carousel Container */}
//             <div className="relative">

//               {/* Cards */}
//               <div className="overflow-hidden">
//                 <motion.div
//                   animate={{ x: `-${currentTestimonialSlide * 100}%` }}
//                   transition={{ duration: 0.5, ease: "easeInOut" }}
//                   className="flex"
//                 >
//                   {Array.from({ length: Math.ceil(testimonials.length / 4) }).map((_, slideIndex) => (
//                     <div key={slideIndex} className="min-w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
//                       {testimonials.slice(slideIndex * 4, slideIndex * 4 + 4).map((testimonial, index) => (
//                         <motion.div
//                           key={testimonial.id}
//                           initial={{ opacity: 0, scale: 0.9, y: 30 }}
//                           whileInView={{ opacity: 1, scale: 1, y: 0 }}
//                           viewport={{ once: true }}
//                           transition={{ delay: index * 0.1 }}
//                           whileHover={{ y: -10, scale: 1.02 }}
//                           className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl"
//                         >
//                           {/* Stars */}
//                           <div className="flex gap-1 mb-4">
//                             {[...Array(5)].map((_, i) => (
//                               <Star
//                                 key={i}
//                                 className={`h-5 w-5 ${
//                                   i < testimonial.rating
//                                     ? 'fill-amber-400 text-amber-400'
//                                     : 'text-white/30'
//                                 }`}
//                               />
//                             ))}
//                           </div>

//                           {/* Content */}
//                           <p className="text-white/90 italic mb-6 text-sm leading-relaxed">
//                             &ldquo;{testimonial.content}&rdquo;
//                           </p>

//                           {/* Author */}
//                           <div className="flex items-center gap-3">
//                             {testimonial.image_url ? (
//                               <img
//                                 src={testimonial.image_url}
//                                 alt={testimonial.name}
//                                 className="w-12 h-12 rounded-full object-cover border-2 border-white/30 shadow-lg"
//                                 onError={(e) => {
//                                   e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.name)}&background=random`;
//                                 }}
//                               />
//                             ) : (
//                               <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center border-2 border-white/30 shadow-lg">
//                                 <span className="text-white font-bold text-lg">
//                                   {testimonial.name.charAt(0)}
//                                 </span>
//                               </div>
//                             )}
//                             <div>
//                               <h4 className="font-bold text-white text-sm">
//                                 {testimonial.name}
//                               </h4>
//                               <p className="text-white/60 text-xs">
//                                 {testimonial.designation}
//                                 {testimonial.company ? ` • ${testimonial.company}` : ''}
//                               </p>
//                             </div>
//                           </div>
//                         </motion.div>
//                       ))}
//                     </div>
//                   ))}
//                 </motion.div>
//               </div>

//               {/* Navigation Buttons */}
//               {testimonials.length > 4 && (
//                 <>
//                   <motion.button
//                     whileHover={{ scale: 1.1 }}
//                     whileTap={{ scale: 0.9 }}
//                     onClick={prevTestimonialSlide}
//                     className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-full shadow-xl flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
//                   >
//                     <ChevronLeft className="w-6 h-6" />
//                   </motion.button>

//                   <motion.button
//                     whileHover={{ scale: 1.1 }}
//                     whileTap={{ scale: 0.9 }}
//                     onClick={nextTestimonialSlide}
//                     className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-full shadow-xl flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
//                   >
//                     <ChevronRight className="w-6 h-6" />
//                   </motion.button>
//                 </>
//               )}

//               {/* Dots Indicator */}
//               {testimonials.length > 4 && (
//                 <div className="flex justify-center gap-2 mt-8">
//                   {Array.from({ length: Math.ceil(testimonials.length / 4) }).map((_, index) => (
//                     <motion.button
//                       key={index}
//                       whileHover={{ scale: 1.2 }}
//                       onClick={() => setCurrentTestimonialSlide(index)}
//                       className={`h-2 rounded-full transition-all duration-300 ${
//                         currentTestimonialSlide === index
//                           ? 'w-8 bg-white'
//                           : 'w-2 bg-white/30'
//                       }`}
//                     />
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>
//         </section>
//       )}

//       <Footer />

//       {/* Custom Animations */}
//       <style jsx>{`
//         @keyframes gradient-shift {
//           0%, 100% {
//             opacity: 0.9;
//           }
//           50% {
//             opacity: 0.7;
//           }
//         }

//         @keyframes float {
//           0%, 100% {
//             transform: translateY(0px);
//           }
//           50% {
//             transform: translateY(-20px);
//           }
//         }

//         @keyframes float-delayed {
//           0%, 100% {
//             transform: translateY(0px);
//           }
//           50% {
//             transform: translateY(-30px);
//           }
//         }

//         @keyframes shine {
//           0% {
//             left: -100%;
//           }
//           100% {
//             left: 200%;
//           }
//         }

//         @keyframes shine-slow {
//           0% {
//             left: -100%;
//           }
//           100% {
//             left: 200%;
//           }
//         }

//         .animate-gradient-shift {
//           animation: gradient-shift 8s ease-in-out infinite;
//         }

//         .animate-float {
//           animation: float 6s ease-in-out infinite;
//         }

//         .animate-float-delayed {
//           animation: float-delayed 8s ease-in-out infinite;
//         }

//         .animate-shine {
//           animation: shine 1.5s;
//         }

//         .animate-shine-slow {
//           animation: shine-slow 2s;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default Home;
